// core/data/premium.ts — Typed seed for premium tier MARKETING COPY only.
//
// 1.23.00: Pro is the one real, sellable tier ($5/mo via Stripe Checkout —
// reusing the pre-existing $5 Stripe Price that already backed the Support
// payment link, so anyone who subscribes through either path lands on the same
// Price and the webhook's STRIPE_PRICE_PRO check grants Pro for both —
// see core/services/billingService.ts). Advanced/Ultra are honest
// "coming soon" — no price is presented as available and there is no
// checkout path for either (see PlanCard.tsx). Real subscription state comes
// from GET /billing/me/ (hooks/useBilling.ts) — nothing in this file is used
// for entitlement decisions, only display copy.
//
// DELETED (do not re-add under any name — owner ruling, selling feed reach
// is forbidden): 'Verified badge' as a purchasable Pro benefit (a checkmark
// is earned via verification + 2FA, never bought — the supporter badge
// below is the real, distinct, still-2FA-gated Pro perk), 'Premium Cherish
// (your cherishes carry more boost)', 'Boosted visibility (feed priority)'.
//
// Fix-round F9/F12: this file may only promise what Pro ACTUALLY delivers —
// supporter badge, cosmetic flair, unlimited edit window, post analytics, and
// funding the platform. No reach, no earnings, no monetization, in seed copy
// OR in component copy (premium-absence.test.ts scans both). And there is no
// annual price: only a monthly Stripe price exists, so `annual` reads
// "Coming soon" everywhere rather than quoting a figure checkout won't charge.

import type { PremiumTier, UltraBenefit } from '@/core/models/premium';

const icoSupporter = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 21s-7-4.35-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.65-9.5 9-9.5 9z"/></svg>`;
const icoFlair = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="8"/><path d="M12 5v14M5 12h14" opacity="0.4"/></svg>`;
const icoEdit = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`;
const icoChart = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 20V4M4 20h16"/><path d="M8 16v-3M12 16V9M16 16v-6"/></svg>`;
const icoSub = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 9a8 8 0 0 1 13-3l3 2M20 15a8 8 0 0 1-13 3l-3-2"/><path d="M17 4v4h-4M7 20v-4h4"/></svg>`;
const icoLock = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>`;
const icoHub = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="9" cy="9" r="3"/><circle cx="17" cy="10" r="2.4"/><path d="M3 19c0-3 2.7-5 6-5s6 2 6 5"/><path d="M15.5 14c2.5 0 5 1.6 5 5"/></svg>`;
const icoApi = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m8 8-4 4 4 4M16 8l4 4-4 4M13 6l-2 12"/></svg>`;
const icoSupport = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 13a8 8 0 0 1 16 0"/><rect x="3" y="13" width="4" height="6" rx="1"/><rect x="17" y="13" width="4" height="6" rx="1"/><path d="M20 19a4 4 0 0 1-4 3h-2"/></svg>`;

export const seedTiers: PremiumTier[] = [
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Support what you use',
    audience: 'Anyone who wants to back the platform directly',
    monthly: '$5',
    // No annual Stripe price exists (settings.STRIPE_PRICE_PRO is monthly
    // only), so annual must never quote a figure — see PlanCard.tsx, which
    // also refuses to start checkout while the annual period is selected.
    annual: 'Coming soon',
    benefits: [
      'Supporter badge (shown once two-factor authentication is on)',
      'Cosmetic profile flair',
      'Unlimited edit window (free accounts get 30 minutes after posting)',
      'Basic post analytics on your own posts',
      'Keeps the platform running: no ads, no data sales',
    ],
  },
  {
    id: 'advanced',
    name: 'Advanced',
    tagline: 'Coming soon',
    audience: 'Creators building an audience',
    monthly: 'Coming soon',
    annual: 'Coming soon',
    benefits: ['Everything in Pro', 'Advanced audience analytics', 'Longer / higher-res video'],
  },
  {
    id: 'ultra',
    name: 'Ultra',
    tagline: 'Coming soon',
    audience: 'Professional creators & businesses',
    monthly: 'Coming soon',
    annual: 'Coming soon',
    benefits: [
      'Everything in Advanced',
      'Paid subscriptions & paywalled posts',
      "Creators' Hub",
      'API & integrations',
      'Dedicated support',
    ],
  },
];

/**
 * The benefit cards shown on the subscribed view.
 *
 * Fix-round F10: this list mixes tiers on purpose — the FIRST FOUR are what
 * Pro actually delivers today; the rest are not built yet. The partition is
 * declared once, in features/premium/meta.ts (PRO_ACTIVE_BENEFIT_TITLES), and
 * SubscribedView renders the two groups under separate headings so a
 * subscriber is never told they have something they don't. If you add a
 * benefit here, decide which group it belongs to there.
 */
export const seedUltraBenefits: UltraBenefit[] = [
  { icon: icoSupporter, title: 'Supporter Badge', blurb: 'A distinct mark once two-factor authentication is on.' },
  { icon: icoFlair, title: 'Cosmetic Flair', blurb: 'A small profile decoration, purely visual.' },
  { icon: icoEdit, title: 'Unlimited Edit Window', blurb: 'Free accounts get 30 minutes after posting.' },
  { icon: icoChart, title: 'Post Analytics', blurb: 'Views, likes and replies on your own posts.' },
  { icon: icoSub, title: 'Paid Subscriptions', blurb: 'Planned, not available yet.' },
  { icon: icoLock, title: 'Paywalled Posts', blurb: 'Planned, not available yet.' },
  { icon: icoHub, title: "Creators' Hub", blurb: 'Planned, not available yet.' },
  { icon: icoApi, title: 'API & Integrations', blurb: 'Planned, not available yet.' },
  { icon: icoSupport, title: 'Dedicated Support', blurb: 'Planned, not available yet.' },
];
